import { useEffect } from "react";
import { observer } from "mobx-react";
import { FormGroup, Label } from "reactstrap";
import Select from "react-select";
import StyledMultiSelectWithSearch from "../../common/StyledMultiSelectWithSearch";
import { automationStore } from "stores";
import { Enum } from "helpers";
import { AutomationType, TriggerType, UpdateRecordList, UpdateRecordApplyToType, UpdateRecruiterField, UpdateRecruiterMode } from "helpers/enum";
import { RappidNodeType } from "helpers/Constants";
import stepUpdateRecordIcon from "assets/img/automation/step-updaterecord.svg";
import HeaderForm from "./HeaderForm";
import SubmitButton from "./SubmitButton";
import { validateNodeAndResetBorder } from "components/Rappid/RappidCanvasFunctions";

const APPLY_TO_LABELS = {
  [UpdateRecordApplyToType.Placements]: "This placement",
  [UpdateRecordApplyToType.Jobs]: "This job",
  [UpdateRecordApplyToType.AllCandidatesInJob]: "All candidates on this job",
  [UpdateRecordApplyToType.AllPlacementsInJob]: "All placements on this job",
};

const SegControl = ({ options, value, onChange, isDisabled }) => (
  <div style={{
    display: 'inline-flex',
    border: '1px solid rgba(255,255,255,.18)',
    borderRadius: 22,
    overflow: 'hidden',
    marginBottom: 10,
  }}>
    {options.map(opt => (
      <button
        key={opt.value}
        type="button"
        disabled={isDisabled}
        onClick={() => onChange(opt.value)}
        style={{
          border: 0,
          padding: '6px 14px',
          fontSize: 12.5,
          background: value === opt.value ? '#ef4277' : 'transparent',
          color: value === opt.value ? '#fff' : '#d6d6d6',
          cursor: isDisabled ? 'default' : 'pointer',
          outline: 'none',
        }}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const getApplyToOptions = (isClient, isJobBased, isPlacementBased) => {
  if (isPlacementBased) {
    return [{ value: UpdateRecordApplyToType.Placements, label: APPLY_TO_LABELS[UpdateRecordApplyToType.Placements] }];
  }
  if (isJobBased && isClient) {
    return [
      { value: UpdateRecordApplyToType.Jobs, label: APPLY_TO_LABELS[UpdateRecordApplyToType.Jobs] },
      { value: UpdateRecordApplyToType.AllPlacementsInJob, label: APPLY_TO_LABELS[UpdateRecordApplyToType.AllPlacementsInJob] },
    ];
  }
  if (isJobBased) {
    return [
      { value: UpdateRecordApplyToType.Jobs, label: APPLY_TO_LABELS[UpdateRecordApplyToType.Jobs] },
      { value: UpdateRecordApplyToType.AllCandidatesInJob, label: APPLY_TO_LABELS[UpdateRecordApplyToType.AllCandidatesInJob] },
      { value: UpdateRecordApplyToType.AllPlacementsInJob, label: APPLY_TO_LABELS[UpdateRecordApplyToType.AllPlacementsInJob] },
    ];
  }
  return null;
};

const UpdateRecruiterForm = observer(() => {
  const { formUpdateRecruiter, automationType, triggerType, automationState, automationAlertAtsUserIds } = automationStore;
  const isDraft = automationState === Enum.AutomationStateType.Draft;
  const isClient = automationType === AutomationType.Client;
  const isJobBased = triggerType === TriggerType.JobBased;
  const isPlacementBased = triggerType === TriggerType.PlacementBased;
  const isCandidateOnly = !isClient && !isJobBased && !isPlacementBased;

  const applyToOptions = getApplyToOptions(isClient, isJobBased, isPlacementBased);
  const title = isCandidateOnly ? 'Update recruiter' : 'Update recruiter / owner';

  useEffect(() => {
    automationStore.getUsers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const defaults = {};
    if (formUpdateRecruiter.recordField == null) defaults.recordField = UpdateRecordList.UpdateRecruiter;
    if (formUpdateRecruiter.applyTo === undefined) defaults.applyTo = applyToOptions ? applyToOptions[0].value : null;
    if (formUpdateRecruiter.recruiterField === undefined) defaults.recruiterField = UpdateRecruiterField.Recruiter;
    if (formUpdateRecruiter.recruiterMode === undefined) defaults.recruiterMode = UpdateRecruiterMode.Add;
    if (formUpdateRecruiter.recruiterIds === undefined) defaults.recruiterIds = [];
    if (Object.keys(defaults).length > 0) {
      automationStore.setFormUpdateRecruiter({ ...formUpdateRecruiter, ...defaults }, true);
    }
    return () => automationStore.resetFormUpdateRecruiter();
    // eslint-disable-next-line
  }, []);

  const selectedApplyTo = formUpdateRecruiter.applyTo ?? (applyToOptions ? applyToOptions[0].value : null);

  // Candidate entities only carry a Recruiter list - Owner is only writable when the target is a job
  // (directly, or indirectly via a placement's linked job), so candidate-only scope forces Recruiter.
  const isRecruiterFieldForced = !isClient && (selectedApplyTo === null || selectedApplyTo === UpdateRecordApplyToType.AllCandidatesInJob);
  const recruiterField = isRecruiterFieldForced ? UpdateRecruiterField.Recruiter : (formUpdateRecruiter.recruiterField || UpdateRecruiterField.Recruiter);
  const recruiterMode = formUpdateRecruiter.recruiterMode || UpdateRecruiterMode.Add;
  const isMultiRecruiter = recruiterField === UpdateRecruiterField.Recruiter;
  const showFieldToggle = !isCandidateOnly && !isRecruiterFieldForced;
  const showMode = isMultiRecruiter;

  let hint;
  if (isCandidateOnly) {
    hint = recruiterMode === UpdateRecruiterMode.Add
      ? 'Adds these to the existing recruiter list.'
      : 'Replaces the existing recruiter list.';
  } else if (isPlacementBased) {
    hint = 'Owner updates the linked job (shown on the placement). Recruiter writes to the placement.';
  } else if (isRecruiterFieldForced) {
    hint = 'Owner is not available for this scope, only Recruiter can be updated here.';
  } else if (isJobBased) {
    hint = 'Owner is a single value — Mode hides when Owner selected.';
  } else {
    hint = 'Owner is single-value; Recruiter is a list with Add / Replace.';
  }

  const handleApplyToChange = (opt) => {
    const data = { ...formUpdateRecruiter, applyTo: opt.value };
    if (!isClient && opt.value === UpdateRecordApplyToType.AllCandidatesInJob && data.recruiterField !== UpdateRecruiterField.Recruiter) {
      data.recruiterField = UpdateRecruiterField.Recruiter;
      data.recruiterMode = UpdateRecruiterMode.Add;
      data.recruiterIds = [];
    }
    automationStore.setFormUpdateRecruiter(data);
  };

  const handleFieldChange = (value) => {
    automationStore.setFormUpdateRecruiter({
      ...formUpdateRecruiter,
      recruiterField: value,
      recruiterMode: value === UpdateRecruiterField.Owner ? UpdateRecruiterMode.Replace : UpdateRecruiterMode.Add,
      recruiterIds: [],
    });
  };

  const handleModeChange = (value) => {
    automationStore.setFormUpdateRecruiter({ ...formUpdateRecruiter, recruiterMode: value });
  };

  const selectedRecruiters = automationAlertAtsUserIds.filter(user => (formUpdateRecruiter.recruiterIds || []).includes(user.id));

  const handleRecruiterChange = (selected) => {
    const recruiterIds = isMultiRecruiter
      ? (selected ? selected.map(s => s.id) : [])
      : (selected ? [selected.id] : []);
    automationStore.setFormUpdateRecruiter({ ...formUpdateRecruiter, recruiterIds });
  };

  const isDisableButton = () => (formUpdateRecruiter.recruiterIds?.length || 0) === 0;

  return (
    <>
      <HeaderForm
        automationHeaderId="form-step-updaterecruiter"
        automationHeaderImgSrc={stepUpdateRecordIcon}
        automationHeaderImgAlt="icon-updaterecruiter"
        automationHeaderTextStep={title}
      />

      {applyToOptions && (
        <FormGroup className="step-body-text">
          <Label className="text-white">Apply to</Label>
          <Select
            id="automation-updaterecruiter-applyto"
            classNamePrefix="automation-dropdown"
            options={applyToOptions}
            isDisabled={!isDraft}
            isSearchable={false}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            onChange={handleApplyToChange}
            value={applyToOptions.find(o => o.value === selectedApplyTo) || applyToOptions[0]}
          />
        </FormGroup>
      )}

      {showFieldToggle && (
        <FormGroup className="step-body-text">
          <Label className="text-white">Field</Label>
          <div>
            <SegControl
              isDisabled={!isDraft}
              options={[
                { label: 'Recruiter', value: UpdateRecruiterField.Recruiter },
                { label: 'Owner', value: UpdateRecruiterField.Owner },
              ]}
              value={recruiterField}
              onChange={handleFieldChange}
            />
          </div>
        </FormGroup>
      )}

      {showMode && (
        <FormGroup className="step-body-text">
          <Label className="text-white">Mode</Label>
          <div>
            <SegControl
              isDisabled={!isDraft}
              options={[
                { label: 'Add', value: UpdateRecruiterMode.Add },
                { label: 'Replace', value: UpdateRecruiterMode.Replace },
              ]}
              value={recruiterMode}
              onChange={handleModeChange}
            />
          </div>
        </FormGroup>
      )}

      <FormGroup className="step-body-text">
        <Label className="text-white">
          {recruiterField === UpdateRecruiterField.Owner ? 'Owner' : 'Recruiter(s)'}
        </Label>
        {isMultiRecruiter ? (
          <StyledMultiSelectWithSearch
            showSelectAll
            showTags
            valueKey="id"
            labelKey="fullName"
            className="automation-form-dropdown-multi"
            id="automation-updaterecruiter-users"
            name="automation-updaterecruiter-users"
            placeholder="Search users…"
            isDisabled={!isDraft}
            options={automationAlertAtsUserIds}
            value={selectedRecruiters}
            onChange={handleRecruiterChange}
          />
        ) : (
          <Select
            id="automation-updaterecruiter-users"
            classNamePrefix="automation-dropdown"
            isDisabled={!isDraft}
            placeholder="Search users…"
            options={automationAlertAtsUserIds}
            getOptionLabel={(option) => option.fullName}
            getOptionValue={(option) => option.id}
            onChange={handleRecruiterChange}
            value={selectedRecruiters[0] || null}
          />
        )}
        <p style={{ fontSize: 11.5, color: '#9a9a9c', margin: '4px 0 0' }}>{hint}</p>
      </FormGroup>

      {isDraft &&
        <SubmitButton
          type={RappidNodeType.UpdateRecord}
          text={setUpdateRecruiterNodeTextLine1(formUpdateRecruiter)}
          text2={setUpdateRecruiterNodeTextLine2(formUpdateRecruiter)}
          roiData={formUpdateRecruiter}
          validateConditions={() => validateNodeAndResetBorder()}
          isDisabled={isDisableButton()} />
      }
    </>
  );
});

export const setUpdateRecruiterNodeTextLine1 = (formUpdateRecruiter) => {
  const fieldLabel = formUpdateRecruiter?.recruiterField === UpdateRecruiterField.Owner ? "Owner" : "Recruiter";
  const applyToLabel = formUpdateRecruiter?.applyTo
    ? APPLY_TO_LABELS[formUpdateRecruiter.applyTo]
    : (automationStore.automationType === AutomationType.Client ? "This contact" : "This candidate");
  return `Update ${fieldLabel} – ${applyToLabel}`;
};

export const setUpdateRecruiterNodeTextLine2 = (formUpdateRecruiter) => {
  const modeLabel = formUpdateRecruiter?.recruiterField === UpdateRecruiterField.Owner
    ? ""
    : (formUpdateRecruiter?.recruiterMode === UpdateRecruiterMode.Replace ? "Replace" : "Add") + ": ";
  const count = formUpdateRecruiter?.recruiterIds?.length || 0;
  return `${modeLabel}${count} user(s)`;
};

export default UpdateRecruiterForm;
